import React, { useState, useEffect } from 'react';
import { Gauge, Activity, RefreshCw, Edit, AlertCircle } from 'lucide-react';
import api from '../../services/api';

const SMPPTPSControl = () => {
  const [tpsStatus, setTpsStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingLimit, setEditingLimit] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [newLimit, setNewLimit] = useState(0);

  useEffect(() => {
    fetchTPSStatus();
    const interval = setInterval(fetchTPSStatus, 3000);
    return () => clearInterval(interval);
  }, []);

  const fetchTPSStatus = async () => {
    try {
      const response = await api.get('/super/smpp/tps/status');
      setTpsStatus(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch TPS status:', error);
      setLoading(false);
    }
  };

  const handleEditLimit = (targetType, targetId, currentLimit, name) => {
    setEditingLimit({ targetType, targetId, name });
    setNewLimit(currentLimit);
    setShowEditModal(true);
  };

  const handleSaveLimit = async () => {
    try {
      await api.post('/super/smpp/tps/update-limit', {
        target_type: editingLimit.targetType,
        target_id: editingLimit.targetId,
        new_limit: newLimit
      });
      alert('TPS limit updated successfully');
      setShowEditModal(false);
      fetchTPSStatus();
    } catch (error) {
      alert('Failed to update TPS limit: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleResetCounters = async (targetType, targetId = null) => {
    if (!window.confirm('Reset TPS counters?')) return;

    try {
      await api.post('/super/smpp/tps/reset-counters', {
        target_type: targetType, target_id: targetId
      });
      alert('TPS counters reset successfully');
      fetchTPSStatus();
    } catch (error) {
      alert('Failed to reset counters: ' + (error.response?.data?.error || error.message));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">TPS & Throttling Control</h1>
          <p className="text-gray-600 mt-2">Monitor and control TPS limits across the system</p>
        </div>
        <button
          onClick={() => window.location.href = '/super/smpp'}
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
        >
          ← Back to SMPP Management
        </button>
      </div>

      {tpsStatus && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-white rounded-lg shadow p-6 border-l-4 border-blue-500">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Global TPS</h3>
                <Gauge className="w-6 h-6 text-blue-500" />
              </div>
              <p className="text-3xl font-bold text-gray-900">{tpsStatus.global_tps}</p>
              <p className="text-sm text-gray-600 mt-2">System-wide limit</p>
            </div>

            <div className="bg-white rounded-lg shadow p-6 border-l-4 border-green-500">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Inbound TPS</h3>
                <Activity className="w-6 h-6 text-green-500" />
              </div>
              <p className="text-3xl font-bold text-gray-900">
                {tpsStatus.current_usage?.total_inbound || 0}
              </p>
              <p className="text-sm text-gray-600 mt-2">Current usage</p>
            </div>

            <div className="bg-white rounded-lg shadow p-6 border-l-4 border-purple-500">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Outbound TPS</h3>
                <Activity className="w-6 h-6 text-purple-500" />
              </div>
              <p className="text-3xl font-bold text-gray-900">
                {tpsStatus.current_usage?.total_outbound || 0}
              </p>
              <p className="text-sm text-gray-600 mt-2">Current usage</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">Operator TPS</h3>
                <button
                  onClick={() => handleResetCounters('all')}
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                >
                  <RefreshCw className="w-4 h-4" />
                  Reset All
                </button>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  {Object.entries(tpsStatus.operator_tps || {}).map(([name, op]) => (
                    <div key={op.connector_id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h4 className="font-medium text-gray-900">{name}</h4>
                          <p className="text-sm text-gray-600">Connector ID: {op.connector_id}</p>
                        </div>
                        <button
                          onClick={() => handleEditLimit('connector', op.connector_id, op.tps_limit, name)}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="grid grid-cols-3 gap-4 mt-3">
                        <div>
                          <p className="text-xs text-gray-600">Limit</p>
                          <p className="text-lg font-semibold text-gray-900">{op.tps_limit}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600">Current</p>
                          <p className="text-lg font-semibold text-green-600">{op.current_tps}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600">Available</p>
                          <p className="text-lg font-semibold text-blue-600">{op.available_slots}</p>
                        </div>
                      </div>
                      <div className="mt-3">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-green-500 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${(op.current_tps / op.tps_limit) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">User TPS (Top 20)</h3>
              </div>
              <div className="p-6">
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {Object.entries(tpsStatus.user_tps || {}).slice(0, 20).map(([username, user]) => (
                    <div key={user.user_id} className="border border-gray-200 rounded-lg p-3">
                      <div className="flex justify-between items-center mb-2">
                        <div>
                          <h4 className="font-medium text-gray-900 text-sm">{username}</h4>
                          <p className="text-xs text-gray-600">ID: {user.user_id}</p>
                        </div>
                        <button
                          onClick={() => handleEditLimit('user', user.user_id, user.tps_limit, username)}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-gray-600">
                          {user.current_tps} / {user.tps_limit} TPS
                        </span>
                        <div className="w-24 bg-gray-200 rounded-full h-1.5">
                          <div
                            className="bg-blue-500 h-1.5 rounded-full"
                            style={{ width: `${(user.current_tps / user.tps_limit) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <h4 className="text-sm font-semibold text-yellow-900 mb-1">
                  TPS Control Guidelines
                </h4>
                <ul className="text-sm text-yellow-800 space-y-1">
                  <li>• TPS limits are enforced in real-time using token bucket algorithm</li>
                  <li>• Changing TPS limits takes effect immediately</li>
                  <li>• Resetting counters will clear current usage statistics</li>
                  <li>• Monitor operator TPS to prevent throttling from carriers</li>
                </ul>
              </div>
            </div>
          </div>
        </>
      )}

      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Update TPS Limit</h3>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                Target: <span className="font-medium text-gray-900">{editingLimit?.name}</span>
              </p>
              <p className="text-sm text-gray-600 mb-4">
                Type: <span className="font-medium text-gray-900">{editingLimit?.targetType}</span>
              </p>
              
              <label className="block text-sm font-medium text-gray-700 mb-1">
                New TPS Limit
              </label>
              <input
                type="number"
                value={newLimit}
                onChange={(e) => setNewLimit(parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                min="1"
                max="10000"
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveLimit}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Update Limit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SMPPTPSControl;
